import React, { useEffect, useRef, useState, useMemo, useCallback, memo } from "react";
import {
  FaTimes,
  FaPlay,
  FaPause,
  FaVolumeUp,
  FaVolumeMute,
  FaExpand,
  FaCompress,
} from "react-icons/fa";
import ReactPlayer from "react-player";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import { usePubSub } from "../../config/sdkConfig";
import useIsMobile from "../../hooks/useIsMobile";

// Detect iOS Safari (not Android)
const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

// Grid-recorder master-stream pub/sub channel: gallery cell publishes a
// MediaStream captured from its real <video> element; mirror cells listen
// for changes and bind their <video srcObject>. One decode, four displays.
const WINDOW_KEY_STREAM = '__rlinkVideoMasterStream';
const WINDOW_EVENT = 'rlink:video-master-stream-change';

const VideoSection = memo(function VideoSection({
  settings = {},
  fileUrl: legacyFileUrl,
  onClosePanel,
  sideBarContainerWidth,
  leftSidebarCollapsed = true,
  rightSidebarCollapsed = true,
  isOwner = false,
  // Grid-recorder: forceMuted mutes audio in non-gallery cells so only one
  // cell drives audio into the PulseAudio sink (prevents 4x echo).
  forceMuted = false,
  // Grid-recorder: forceHide skips rendering the video element entirely in
  // non-gallery cells. Without this, each of the 4 cells runs its own
  // 1080p video decoder on the same source (~0.5 core × 4 = 2 cores of
  // redundant work), starving the shared Xvfb/Chrome main thread and
  // causing visible stutter. Gallery keeps full video; focus/speaker-top/
  // speaker-side layouts show the meeting without the video overlay.
  forceHide = false,
  // Recorder bot: skip header/sidebar/footer offsets so the video fills
  // the captured frame edge-to-edge. Without this, the fullscreen branch
  // applies +56px sidebar + 12px gap + 64px header + 76px footer, which
  // produced a ~140×140 px dark border around every recorded video.
  isRecorderMode = false,
}) {
  const playerRef = useRef(null);
  const containerRef = useRef(null);
  const mountedRef = useRef(true);
  // Track whether the user explicitly paused so ReactPlayer's onPlay
  // event (which can fire spuriously for YouTube/HLS) doesn't override it.
  const userPausedRef = useRef(false);
  const iosUserUnmutedRef = useRef(false); // Track if iOS attendee already unmuted
  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [muted, setMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [needsIOSTap, setNeedsIOSTap] = useState(false);
  // iOS: video autoplays muted, show "Tap for audio" to unmute
  const [iosNeedsUnmute, setIosNeedsUnmute] = useState(false);
  const { sideBarMode } = useMeetingAppContext();
  const isMobile = useIsMobile();

  // PubSub for syncing video controls from host to all participants
  const ignorePubSubRef = useRef(false);
  const { publish: publishVideoControl } = usePubSub("VIDEO_CONTROL", {
    onMessageReceived: (data) => {
      if (!mountedRef.current) return;
      // Ignore messages published by this instance (owner)
      if (data?.message?.isLocal) return;
      try {
        const msg = typeof data.message === 'string' ? JSON.parse(data.message) : data.message;
        ignorePubSubRef.current = true;
        switch (msg.action) {
          case 'play': {
            userPausedRef.current = false;
            if (isIOS() && !iosUserUnmutedRef.current) {
              // iOS: first time — start muted with unmute prompt
              setMuted(true);
              setPlaying(true);
              setIosNeedsUnmute(true);
            } else {
              // Non-iOS OR attendee already unmuted — play with current mute state
              setPlaying(true);
            }
            const playInternal = playerRef.current?.getInternalPlayer();
            if (playInternal?.play) playInternal.play().catch(() => {});
            break;
          }
          case 'pause': {
            userPausedRef.current = true;
            // Pause the internal player FIRST (instant), then update React state
            const pauseInternal = playerRef.current?.getInternalPlayer();
            if (pauseInternal) {
              pauseInternal.pause();
              // iOS: also force the currentTime to freeze immediately
              if (isIOS()) {
                const currentTime = pauseInternal.currentTime;
                pauseInternal.currentTime = currentTime;
              }
            }
            setPlaying(false);
            break;
          }
          case 'mute':
            setMuted(msg.muted);
            break;
          case 'seek':
            playerRef.current?.seekTo(msg.time, "seconds");
            break;
          default:
            break;
        }
        ignorePubSubRef.current = false;
      } catch (e) {
        ignorePubSubRef.current = false;
      }
    },
  });

  // Parse settings once - use stable reference
  const parsedSettings = useMemo(() => {
    if (!settings) return {};
    if (typeof settings === 'string') {
      try {
        return JSON.parse(settings);
      } catch (e) {
        console.error('VideoSection - Failed to parse settings:', e);
        return {};
      }
    }
    return settings;
  }, [settings]);

  // Extract all settings with stable references
  const videoUrl = useMemo(() => {
    const url = parsedSettings.file_url || parsedSettings.fileUrl || legacyFileUrl || "";
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }
    return `https://${url}`;
  }, [parsedSettings.file_url, parsedSettings.fileUrl, legacyFileUrl]);

  const displayMode = parsedSettings.display_mode || "fullscreen";
  const playback = parsedSettings.playback || {};
  const pipSettings = parsedSettings.pip_settings || { position: "top_left", size: 25 };

  const autoplay = playback.autoplay !== false;
  const loop = playback.loop || false;
  const audioMode = playback.audio_mode || "normal";
  const volume = audioMode === "muted" ? 0 : (playback.volume || 100) / 100;
  const startAt = playback.start_at || 0;
  const endAt = playback.end_at || null;

  // Calculate sidebar widths
  const leftSidebarWidth = leftSidebarCollapsed ? 56 : 380;
  const rightSidebarWidth = rightSidebarCollapsed ? 56 : 300;

  // Check display modes
  const isPipMode = displayMode === "pip";
  const isBackgroundMode = displayMode === "background";
  const isFullscreenMode = !isPipMode && !isBackgroundMode;

  useEffect(() => {
    mountedRef.current = true;
    // Always start fresh — user hasn't paused yet on this mount
    userPausedRef.current = false;

    // Initialize muted state
    setMuted(audioMode === "muted");

    return () => {
      mountedRef.current = false;
    };
  }, []); // Empty deps - only run on mount/unmount

  // Handle fullscreen changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (mountedRef.current) {
        setIsFullscreen(!!document.fullscreenElement);
      }
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  // Handlers with stable references
  const handleReady = useCallback(() => {
    if (!mountedRef.current) return;
    setReady(true);
    if (autoplay && !userPausedRef.current) {
      if (isIOS()) {
        // iOS: autoplay muted (always works), then show "Tap for audio"
        setMuted(true);
        setPlaying(true);
        if (audioMode !== "muted") {
          setIosNeedsUnmute(true);
        }
      } else {
        setPlaying(true);
      }
    }
    if (startAt > 0 && playerRef.current) {
      playerRef.current.seekTo(startAt, "seconds");
    }
  }, [autoplay, startAt, audioMode]);

  // Grid-recorder master-stream publish: gallery cell (forceHide=false)
  // captures its real <video> element's output as a MediaStream, stashes
  // it on window + fires an event so mirror cells can bind their
  // <video srcObject={stream}>. Works off the `playing` signal (not
  // `ready`) because `ready` can fire before the <video> DOM element
  // is queryable. Uses a short polling retry for the element lookup in
  // case ReactPlayer hasn't attached it to the DOM yet.
  useEffect(() => {
    if (forceHide) return; // only the gallery cell publishes
    if (typeof window === 'undefined') return;
    const isRecorder = new URLSearchParams(window.location.search).get('recorder') === 'true';
    if (!isRecorder) return;
    if (!videoUrl) return;

    let cancelled = false;
    let attemptTimer = null;
    let publishedStream = null;
    let attempts = 0;

    const tryPublish = () => {
      if (cancelled || publishedStream) return;
      attempts += 1;
      const videoEl = containerRef.current?.querySelector('video');
      if (!videoEl) {
        console.log('[VideoSection-Recorder] attempt', attempts, ': <video> not found yet');
      } else if (typeof videoEl.captureStream !== 'function') {
        console.warn('[VideoSection-Recorder] captureStream() API not available on this <video>');
        return; // nothing to retry, the DOM node is wrong type
      } else {
        try {
          const stream = videoEl.captureStream();
          if (stream && stream.getTracks().length > 0) {
            publishedStream = stream;
            window[WINDOW_KEY_STREAM] = stream;
            window.dispatchEvent(new CustomEvent(WINDOW_EVENT, { detail: stream }));
            console.log('[VideoSection-Recorder] ✓ published master stream on attempt',
              attempts, '— tracks:', stream.getTracks().map(t => t.kind).join(','));
            return;
          }
          console.log('[VideoSection-Recorder] attempt', attempts, ': captureStream returned', stream ? 'stream w/0 tracks' : 'null');
        } catch (err) {
          console.warn('[VideoSection-Recorder] attempt', attempts, ': captureStream threw:', err?.message);
          return;
        }
      }
      if (attempts < 30) attemptTimer = setTimeout(tryPublish, 500);
      else console.warn('[VideoSection-Recorder] giving up after 30 attempts (~15s)');
    };

    // Kick off — try immediately, poll every 500ms until we get a stream
    // with tracks OR we run out of attempts. 30 × 500ms = 15s upper bound.
    tryPublish();

    return () => {
      cancelled = true;
      if (attemptTimer) clearTimeout(attemptTimer);
      if (publishedStream && window[WINDOW_KEY_STREAM] === publishedStream) {
        window[WINDOW_KEY_STREAM] = null;
        window.dispatchEvent(new CustomEvent(WINDOW_EVENT, { detail: null }));
        console.log('[VideoSection-Recorder] cleared master stream on unmount');
      }
    };
  }, [videoUrl, forceHide]);

  // Grid-recorder playback diagnostic: log the gallery <video>'s internal
  // state every 2s. Tells us if the source playback itself is stalling
  // (buffer underruns / CPU starvation / network stall) vs capture-side
  // jitter. readyState<4 or non-advancing currentTime = source playback
  // issue; everything else means capture pipeline is the bottleneck.
  useEffect(() => {
    if (forceHide) return;
    if (typeof window === 'undefined') return;
    const isRecorder = new URLSearchParams(window.location.search).get('recorder') === 'true';
    if (!isRecorder) return;
    if (!videoUrl) return;

    let lastTime = -1;
    let tick = 0;
    const iv = setInterval(() => {
      const v = containerRef.current?.querySelector('video');
      if (!v) return;
      tick += 1;
      const delta = v.currentTime - lastTime;
      lastTime = v.currentTime;
      const buffered = v.buffered && v.buffered.length > 0
        ? v.buffered.end(v.buffered.length - 1).toFixed(1) + 's'
        : 'none';
      console.log(
        '[VideoSection-Playback] tick=' + tick +
        ' t=' + v.currentTime.toFixed(2) +
        ' delta=' + delta.toFixed(2) +
        ' rate=' + v.playbackRate +
        ' ready=' + v.readyState +    // 4 = enough, <4 = data starved
        ' buffer=' + buffered +
        ' paused=' + v.paused +
        ' stalled=' + (delta < 1.0 && !v.paused ? 'YES' : 'no')
      );
    }, 2000);
    return () => clearInterval(iv);
  }, [forceHide, videoUrl]);

  // Defensive ready-fallback: ReactPlayer's onReady is unreliable for some
  // formats (HLS, certain S3 mp4s, mobile Safari) — when it never fires the
  // loading overlay sticks forever, showing the entire video as a black
  // screen with a purple spinner. After 3s without onReady or onError, drop
  // the overlay anyway. The actual video frame will render through it once
  // the underlying <video> element starts playing.
  useEffect(() => {
    if (ready) return;
    const timer = setTimeout(() => {
      if (mountedRef.current && !ready) {
        console.log('[VideoSection] Ready fallback fired — clearing loading overlay');
        setReady(true);
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [ready, videoUrl]);

  // ReactPlayer's `onStart` fires when the first frame actually plays —
  // most reliable signal that the video is rendering. Use it as a backup
  // to onReady so the spinner clears the moment the video is visible.
  const handleStart = useCallback(() => {
    if (mountedRef.current && !ready) {
      console.log('[VideoSection] onStart fired — clearing loading overlay');
      setReady(true);
    }
  }, [ready]);

  const handleProgress = useCallback((state) => {
    if (!mountedRef.current) return;
    setProgress(state.playedSeconds);
    if (endAt && state.playedSeconds >= endAt) {
      if (loop) {
        playerRef.current?.seekTo(startAt, "seconds");
      } else {
        setPlaying(false);
      }
    }
  }, [endAt, loop, startAt]);

  const handleDuration = useCallback((dur) => {
    if (mountedRef.current) {
      setDuration(dur);
    }
  }, []);

  const handleEnded = useCallback(() => {
    if (!mountedRef.current) return;
    console.log("VideoSection - handleEnded, loop:", loop);
    if (loop) {
      userPausedRef.current = false;
      playerRef.current?.seekTo(startAt, "seconds");
      setPlaying(true);
    } else {
      setPlaying(false);
    }
  }, [loop, startAt]);

  const handleError = useCallback((error) => {
    console.error("ReactPlayer error:", error);
    if (mountedRef.current) {
      setReady(true);
    }
  }, []);

  const handlePlay = useCallback(() => {
    // Don't override an explicit user pause — some players (YouTube, HLS)
    // fire onPlay events spuriously after being told to pause.
    if (mountedRef.current && !userPausedRef.current) setPlaying(true);
  }, []);

  const handlePause = useCallback(() => {
    if (mountedRef.current) setPlaying(false);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  const togglePlay = useCallback(() => {
    setNeedsIOSTap(false);
    setPlaying(prev => {
      const next = !prev;
      // Track user intent: if pausing, set the ref so handlePlay won't override it
      userPausedRef.current = !next;
      // Broadcast to all participants if owner
      if (isOwner) {
        publishVideoControl(JSON.stringify({ action: next ? 'play' : 'pause' }));
      }
      return next;
    });
  }, [isOwner, publishVideoControl]);

  const toggleMute = useCallback(() => {
    setMuted(prev => {
      const next = !prev;
      if (isOwner) {
        publishVideoControl(JSON.stringify({ action: 'mute', muted: next }));
      }
      return next;
    });
  }, [isOwner, publishVideoControl]);

  // iOS: unmute video on user tap (user gesture required by iOS)
  const handleIOSUnmute = useCallback((e) => {
    e?.stopPropagation();
    setIosNeedsUnmute(false);
    iosUserUnmutedRef.current = true;

    // iOS fix: simply changing muted prop leaves audio at low volume.
    // Replicating the exact sequence that produces full audio (pause → unmute → play):
    const internal = playerRef.current?.getInternalPlayer();
    if (internal) {
      internal.pause();
      internal.muted = false;
      internal.volume = 1.0;
      setTimeout(() => {
        internal.play().catch(() => {});
        setMuted(false);
        setPlaying(true);
      }, 50);
    } else {
      setMuted(false);
      if (!playing) setPlaying(true);
    }
  }, [playing]);

  const handleClose = useCallback((e) => {
    e?.stopPropagation();
    onClosePanel?.();
  }, [onClosePanel]);

  // Format time for display
  const formatTime = useCallback((seconds) => {
    if (!seconds || isNaN(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }, []);

  // ReactPlayer config - stable reference
  const playerConfig = useMemo(() => ({
    file: {
      attributes: {
        preload: "auto",
      },
      forceVideo: true,
    },
  }), []);

  // Container styles based on display mode
  const containerStyles = useMemo(() => {
    if (isPipMode) {
      const pipSize = Math.min(pipSettings.size || 25, 25);
      const position = pipSettings.position || 'top_left';
      const headerHeight = 70;
      const footerHeight = 85;
      const leftOffset = isMobile ? 10 : leftSidebarWidth + 20;
      const rightOffset = isMobile ? 10 : rightSidebarWidth + (sideBarMode ? sideBarContainerWidth : 0) + 20;

      const baseStyles = {
        position: 'fixed',
        width: isMobile ? '35%' : `${pipSize}%`,
        maxWidth: isMobile ? '160px' : '300px',
        minWidth: '140px',
        aspectRatio: '16/9',
        zIndex: 99999999,
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
        backgroundColor: '#000',
      };

      switch (position) {
        case 'top_left':
          return { ...baseStyles, top: headerHeight, left: leftOffset };
        case 'top_right':
          return { ...baseStyles, top: headerHeight, right: rightOffset };
        case 'bottom_left':
          return { ...baseStyles, bottom: footerHeight, left: leftOffset };
        case 'bottom_right':
        default:
          return { ...baseStyles, bottom: footerHeight, right: rightOffset };
      }
    }

    if (isBackgroundMode) {
      return {
        position: "absolute",
        top: 0,
        left: isRecorderMode ? 0 : (isMobile ? 0 : leftSidebarWidth),
        right: isRecorderMode ? 0 : (isMobile ? 0 : rightSidebarWidth + (sideBarMode ? sideBarContainerWidth : 0)),
        bottom: 0,
        zIndex: 1,
        backgroundColor: "#000",
      };
    }

    // Fullscreen mode
    return {
      position: isRecorderMode ? "absolute" : (isMobile ? "fixed" : "absolute"),
      top: isRecorderMode ? 0 : (isMobile ? 64 : 64),
      left: isRecorderMode ? 0 : (isMobile ? 0 : leftSidebarWidth + 12),
      right: isRecorderMode ? 0 : (isMobile ? 0 : rightSidebarWidth + (sideBarMode ? sideBarContainerWidth : 0) + 12),
      bottom: isRecorderMode ? 0 : (isMobile ? 80 : 76),
      zIndex: 99999999,
      backgroundColor: "#000",
      borderRadius: isMobile ? 0 : "12px",
      overflow: "hidden",
    };
  }, [isPipMode, isBackgroundMode, pipSettings, isMobile, leftSidebarWidth, rightSidebarWidth, sideBarMode, sideBarContainerWidth, isRecorderMode]);

  // Don't render if no URL
  if (!videoUrl) {
    return null;
  }

  // Grid-recorder: non-gallery cells skip the ReactPlayer + <video> instance
  // to eliminate redundant 1080p decodes. Instead they render a mirror
  // <video> whose srcObject is the MediaStream captured from gallery's
  // real <video> element — same frames, zero extra decoding.
  if (forceHide) {
    return <RecorderVideoMirror objectFit={settings?.objectFit || 'contain'} />;
  }

  return (
    <div
      ref={containerRef}
      style={containerStyles}
      className={`group ${isBackgroundMode ? 'opacity-30' : ''}`}
    >
      {/* Video Player */}
      <div className={`relative w-full h-full ${isBackgroundMode ? 'blur-sm' : ''}`}>
        <ReactPlayer
          ref={playerRef}
          url={videoUrl}
          width="100%"
          height="100%"
          playing={playing}
          muted={muted || forceMuted}
          volume={forceMuted ? 0 : volume}
          loop={false}
          onReady={handleReady}
          onStart={handleStart}
          onProgress={handleProgress}
          onDuration={handleDuration}
          onEnded={handleEnded}
          onError={handleError}
          onPlay={handlePlay}
          onPause={handlePause}
          controls={false}
          config={playerConfig}
          playsinline
          progressInterval={1000}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
          }}
        />

        {/* Controls Overlay - only shown to the person who enabled the video */}
        {isOwner && (
        <div
          className={`absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/40
            transition-opacity duration-300 ${!playing || !ready ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
        >
          {/* Close Button */}
          <div className="absolute top-2 right-2 z-50">
            <button
              onClick={handleClose}
              className={`rounded-full bg-red-600 hover:bg-red-500 flex items-center justify-center text-white transition-colors cursor-pointer ${
                isPipMode ? "w-7 h-7" : "w-9 h-9"
              }`}
            >
              <FaTimes size={isPipMode ? 12 : 16} />
            </button>
          </div>

          {/* Center Play Button */}
          {!isPipMode && !playing && ready && (
            <div className="absolute inset-0 flex items-center justify-center z-40">
              <button
                onClick={(e) => { e.stopPropagation(); togglePlay(); }}
                className="w-16 h-16 rounded-full bg-white/20 backdrop-blur hover:bg-white/30 flex items-center justify-center text-white transition-colors cursor-pointer"
              >
                <FaPlay size={24} className="ml-1" />
              </button>
            </div>
          )}

          {/* Bottom Controls */}
          <div className={`absolute bottom-0 left-0 right-0 z-40 ${isPipMode ? "p-2" : "p-3"}`}>
            {/* Progress Bar */}
            {!isPipMode && (
              <div className="mb-3">
                <div
                  className="h-1 bg-white/30 rounded-full cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    const rect = e.currentTarget.getBoundingClientRect();
                    const percent = (e.clientX - rect.left) / rect.width;
                    const seekTime = percent * duration;
                    playerRef.current?.seekTo(seekTime, "seconds");
                    if (isOwner) {
                      publishVideoControl(JSON.stringify({ action: 'seek', time: seekTime }));
                    }
                  }}
                >
                  <div
                    className="h-full bg-purple-500 rounded-full"
                    style={{ width: `${(progress / (duration || 1)) * 100}%` }}
                  />
                </div>
              </div>
            )}

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => { e.stopPropagation(); togglePlay(); }}
                  className={`rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors cursor-pointer ${
                    isPipMode ? "w-7 h-7" : "w-8 h-8"
                  }`}
                >
                  {playing ? <FaPause size={isPipMode ? 10 : 12} /> : <FaPlay size={isPipMode ? 10 : 12} className="ml-0.5" />}
                </button>

                <button
                  onClick={(e) => { e.stopPropagation(); toggleMute(); }}
                  className={`rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors cursor-pointer ${
                    isPipMode ? "w-7 h-7" : "w-8 h-8"
                  }`}
                >
                  {muted ? <FaVolumeMute size={isPipMode ? 12 : 14} /> : <FaVolumeUp size={isPipMode ? 12 : 14} />}
                </button>

                {!isPipMode && (
                  <span className="text-white text-xs">
                    {formatTime(progress)} / {formatTime(duration)}
                  </span>
                )}
              </div>

              {!isPipMode && (
                <button
                  onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }}
                  className="w-8 h-8 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white transition-colors cursor-pointer"
                >
                  {isFullscreen ? <FaCompress size={12} /> : <FaExpand size={12} />}
                </button>
              )}
            </div>
          </div>
        </div>
        )}

        {/* iOS Tap-to-play overlay — shown only on iPhone/iPad when autoplay is blocked */}
        {needsIOSTap && ready && (
          <div className="absolute inset-0 flex items-center justify-center z-50 bg-black/30">
            <button
              onClick={(e) => { e.stopPropagation(); togglePlay(); }}
              className="flex flex-col items-center gap-3 group"
            >
              <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm border-2 border-white/50 flex items-center justify-center group-active:scale-95 transition-transform">
                <FaPlay size={28} className="text-white ml-1.5" />
              </div>
              <span className="text-white text-sm font-medium bg-black/40 backdrop-blur-sm px-4 py-1.5 rounded-full">
                Tap to play video
              </span>
            </button>
          </div>
        )}

        {/* iOS "Tap for audio" overlay — video is playing muted, tap to unmute */}
        {iosNeedsUnmute && playing && ready && (
          <div className="absolute bottom-16 left-0 right-0 flex justify-center z-50">
            <button
              onClick={handleIOSUnmute}
              className="flex items-center gap-2 bg-black/60 backdrop-blur-sm border border-white/20 px-4 py-2 rounded-full active:scale-95 transition-transform"
            >
              <FaVolumeMute size={14} className="text-white" />
              <span className="text-white text-sm font-medium">Tap for audio</span>
            </button>
          </div>
        )}

        {/* Loading Indicator — semi-transparent so the video frame is still
            visible underneath if the spinner gets stuck (defense against
            onReady/onStart never firing for some formats). pointer-events:none
            so a stuck spinner can't block clicks on the video below. */}
        {!ready && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60 pointer-events-none">
            <div className={`border-4 border-purple-500 border-t-transparent rounded-full animate-spin ${
              isPipMode ? "w-8 h-8" : "w-12 h-12"
            }`} />
          </div>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if these specific props changed
  return (
    prevProps.settings === nextProps.settings &&
    prevProps.fileUrl === nextProps.fileUrl &&
    prevProps.leftSidebarCollapsed === nextProps.leftSidebarCollapsed &&
    prevProps.rightSidebarCollapsed === nextProps.rightSidebarCollapsed &&
    prevProps.sideBarContainerWidth === nextProps.sideBarContainerWidth &&
    prevProps.isOwner === nextProps.isOwner &&
    prevProps.forceMuted === nextProps.forceMuted &&
    prevProps.forceHide === nextProps.forceHide
  );
});

// ────────────────────────────────────────────────────────────────────────
// RecorderVideoMirror — used by grid-recorder non-gallery cells.
// Displays the same video frames the gallery cell is playing, without
// decoding the source a second time. Gallery's real <video> element
// exposes its output via HTMLVideoElement.captureStream(); we publish
// that MediaStream on a window global + event, and mirror cells bind
// their own <video> to it via srcObject. One decode, four display
// surfaces.
//
// The mirror <video> is hard-muted — only the gallery cell drives audio
// into the PulseAudio sink (echo already eliminated via forceMuted on
// the gallery-side ReactPlayer). WINDOW_KEY_STREAM and WINDOW_EVENT are
// declared at module top.

function RecorderVideoMirror({ objectFit = 'contain' }) {
  const ref = useRef(null);
  const [stream, setStream] = useState(() =>
    (typeof window !== 'undefined' && window[WINDOW_KEY_STREAM]) || null);

  useEffect(() => {
    const handler = (e) => setStream(e.detail || null);
    window.addEventListener(WINDOW_EVENT, handler);
    return () => window.removeEventListener(WINDOW_EVENT, handler);
  }, []);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (stream) {
      if (v.srcObject !== stream) {
        v.srcObject = stream;
        v.play().catch(() => { /* harmless; autoplay policy */ });
        console.log('[VideoSection-Mirror] bound stream —', stream.getTracks().length, 'tracks');
      }
    } else {
      // Clear both srcObject AND force a reload so the compositor drops
      // the last-displayed frame. Without load(), Chrome often leaves the
      // final frame visible indefinitely, giving viewers a "frozen stale
      // frame" impression when the video element is closed.
      if (v.srcObject) {
        v.srcObject = null;
        try { v.load(); } catch (_) {}
        console.log('[VideoSection-Mirror] cleared stream + flushed buffer');
      }
    }
  }, [stream]);

  return (
    <div className="relative w-full h-full bg-black flex items-center justify-center">
      <video
        ref={ref}
        autoPlay
        muted
        playsInline
        style={{
          width: '100%',
          height: '100%',
          objectFit,
          backgroundColor: '#000',
        }}
      />
    </div>
  );
}

export default VideoSection;
